/**
 * Upload Speakers Dialog
 *
 * Reason: Allows admins to upload a CSV file with speaker names and emails
 * to automatically create users with role "user" and match them to segments
 */

import { FC, useState, useContext, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  CircularProgress,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { useQueryClient } from 'react-query';
import {
  useUploadSpeakers,
  usePreviewSpeakers,
  type SpeakersPreviewResponse,
} from '@/adapters/speakers';
import { LIST_ORG_USERS_CACHE_KEY } from '@/adapters/users-v2';
import { UserContext } from '@/contexts/UserContext';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface UploadSpeakersDialogProps {
  open: boolean;
  onClose(): void;
}

/**
 * Dialog for uploading speakers CSV file
 *
 * @reason Provides UI for admins to upload CSV and see matching results
 */
const UploadSpeakersDialog: FC<UploadSpeakersDialogProps> = ({ open, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<SpeakersPreviewResponse | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    processed: number;
    success: number;
    failed: number;
    totalMatchedSegments: number;
    results: {
      speaker: { name: string; email: string };
      userId?: string;
      created: boolean;
      matchedSegments: number;
      errors?: string[];
    }[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { userState } = useContext(UserContext);
  const queryClient = useQueryClient();
  const uploadSpeakersMutation = useUploadSpeakers(userState?.orgId);
  const previewSpeakersMutation = usePreviewSpeakers(userState?.orgId);

  /**
   * Handles file selection
   */
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
      setPreviewResult(null);
      // Fetch preview to show matching segments
      previewSpeakersMutation.mutate(file, {
        onSuccess: (data) => setPreviewResult(data),
        onError: () => setPreviewResult(null),
      });
    }
  };

  /**
   * Handles file upload and processing
   */
  const handleUpload = async (): Promise<void> => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setError(null);
    setUploadResult(null);

    try {
      const result = await uploadSpeakersMutation.mutateAsync(selectedFile);
      setUploadResult(result);

      // Invalidate user list to refresh
      await queryClient.invalidateQueries(LIST_ORG_USERS_CACHE_KEY);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload speakers';
      setError(errorMessage);
    }
  };

  /**
   * Handles dialog close and resets form
   */
  const handleClose = (): void => {
    setSelectedFile(null);
    setError(null);
    setUploadResult(null);
    setPreviewResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Speakers Sheet</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {uploadResult && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Upload Complete!
            </Typography>
            <Typography variant="body2">Processed: {uploadResult.processed} speakers</Typography>
            <Typography variant="body2">
              Successful: {uploadResult.success} | Failed: {uploadResult.failed}
            </Typography>
            <Typography variant="body2">
              Total segment mappings created: {uploadResult.totalMatchedSegments}
            </Typography>
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Upload a CSV file with speaker names and emails. The format should be:
          </Typography>
          <Box
            component="pre"
            sx={{
              p: 1,
              bgcolor: 'grey.100',
              borderRadius: 1,
              fontSize: '0.875rem',
              overflow: 'auto',
            }}
          >
            {`name,email
John Doe,john@example.com
Jane Smith,jane@example.com`}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="speaker-csv-upload"
          />
          <label htmlFor="speaker-csv-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={uploadSpeakersMutation.isLoading}
              fullWidth
            >
              {selectedFile ? selectedFile.name : 'Select CSV File'}
            </Button>
          </label>
        </Box>

        {previewSpeakersMutation.isLoading && selectedFile && !previewResult && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Loading segment matches...
          </Typography>
        )}

        {previewResult && !uploadResult && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Segment matches (before upload):
            </Typography>
            <List
              dense
              sx={{ maxHeight: 220, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1, p: 0 }}
            >
              {previewResult.preview.map((item, index) => (
                <Box key={index}>
                  <ListItem>
                    <ListItemText
                      primary={`${item.speaker.name} (${item.speaker.email})`}
                      secondary={
                        item.matchedCount > 0
                          ? `${item.matchedCount} segment(s) will match: ${item.matchedSegments
                              .map((s) => s.segmentName || 'Untitled')
                              .join(', ')}`
                          : 'No matching segments found (speaker name must match segment speaker)'
                      }
                      secondaryTypographyProps={{
                        color: item.matchedCount > 0 ? 'text.secondary' : 'warning.main',
                      }}
                    />
                  </ListItem>
                  {index < previewResult.preview.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Total: {previewResult.totalMatchedSegments} segment mapping(s) will be created
            </Typography>
          </Box>
        )}

        {uploadResult && uploadResult.results.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Upload Results:
            </Typography>
            <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
              {uploadResult.results.map((result, index) => (
                <Box key={index}>
                  <ListItem>
                    <ListItemText
                      primary={`${result.speaker.name} (${result.speaker.email})`}
                      secondary={
                        result.errors && result.errors.length > 0
                          ? `Errors: ${result.errors.join(', ')}`
                          : result.created
                          ? `Created • ${result.matchedSegments} segments matched`
                          : `Updated • ${result.matchedSegments} segments matched`
                      }
                    />
                  </ListItem>
                  {index < uploadResult.results.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </Box>
        )}

        {uploadSpeakersMutation.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
              Processing speakers and matching segments...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploadSpeakersMutation.isLoading}>
          {uploadResult ? 'Close' : 'Cancel'}
        </Button>
        {!uploadResult && (
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploadSpeakersMutation.isLoading}
          >
            Upload
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadSpeakersDialog;
